<!-- A DocumentList displays a list of documents -->
<template>
  <panel v-if="dataSynchronizationComplete" :height="calculatedHeight" :isLoading="isLoading" :drawer="drawer">
    <template v-slot:header>
      <!-- Use panel header background -->
      <div class="d-flex flex-column full-height full-width">
        <div class="d-flex align-center">
          <div class="pt-1 pb-0 flex-grow-1" style="font-size: 16px; line-height: 36px">{{ mx_translateIfExist(title) }}</div>

          <v-btn style="transform: translateY(8px)" icon @click.stop="toggleDrawer" v-if="hasEditableProperties">
            <morph-icon :size="36" to-icon="mdi-close" from-icon="mdi-cog-outline" :trigger="drawer"></morph-icon>
          </v-btn>
        </div>
        <div
          v-if="selectedModules.length === 0"
          class="d-flex caption align-center"
          style="line-height: 24px; transform: translateY(-4px); font-size: 10px; white-space: nowrap"
        >
          {{ $t('widgets.allModules') }}
        </div>
        <div
          v-else
          class="d-flex caption overflow-x-auto align-center"
          style="line-height: 24px; transform: translateY(-4px); font-size: 10px; white-space: nowrap"
        >
          Module:
          <div>
            {{ filteredModuleTitles }}
            <!--            <div v-for="(moduleName, index) in selectedModuleNames" :key="index">{{ moduleName }}</div>-->
          </div>
        </div>
      </div>
    </template>

    <template v-slot:drawer>
      <div class="document-list-drawer" :style="xs ? '' : 'border-radius:10px'">
        <!-- "header" -->
        <div class="d-flex flex-row align-center" :class="theme.elements.toolbarBg">
          <n-title class="ml-3 mt-2">
            {{ $t('widgets.resultItemsCount', { count: filteredAndSortedDocuments.length }) }}
          </n-title>
          <v-spacer></v-spacer>
          <div class="d-flex justify-end">
            <v-btn class="mr-3 mt-4 mb-3" small outlined rounded @click.stop="resetConfig()">
              {{ $t('widgets.resetConfig') }}
            </v-btn>
            <v-btn class="mr-3 mt-4 mb-3" small outlined rounded @click.stop="drawer = false"> {{ $t('cancel') }} </v-btn>
            <v-btn class="mr-3 mt-4 mb-3" small outlined rounded @click.stop="saveConfig()">
              {{ $t('button.save') }}
            </v-btn>
          </div>
        </div>
        <v-divider></v-divider>
        <div class="scrollable-drawer background lighten-1">
          <!-- Title -->
          <n-list-title v-if="config.userTitleEditingEnabled" class="ml-3 mt-8" :text="$t('title')"></n-list-title>

          <v-text-field
            :style="{ maxWidth: '600px' }"
            v-if="config.userTitleEditingEnabled"
            :dark="darkMode"
            clearable
            hide-details
            class="mx-3"
            v-model="title_"
          ></v-text-field>

          <!-- module selection -->
          <module-selection v-if="config.userModuleSelectionEnabled" :config="config" v-model="selectedModules_" :modules="availableModules"></module-selection>

          <!-- unread,  favorites, acknowledgeablee-->
          <n-list-title
            v-if="
              config.userUnreadOnlySelectionEnabled ||
              config.userMandatoryToReadOnlySelectionEnabled ||
              config.userFavoritesSelectionEnabled ||
              config.userAcknowledgeSelectionEnabled ||
              config.userDisplayMandatoryToReadAlsoWhenReadEnabled
            "
            class="ml-3 mt-8"
            :text="unreadAndFavoritesTitle"
          ></n-list-title>
          <div class="d-flex flex-row flex-wrap pt-0 mt-0">
            <v-checkbox
              v-if="config.userUnreadOnlySelectionEnabled"
              class="ml-3"
              v-model="unreadOnly_"
              left
              hide-details
              :label="$t('filter.unreadOnly')"
            ></v-checkbox>
            <v-checkbox
              v-if="config.userMandatoryToReadOnlySelectionEnabled"
              class="ml-3"
              v-model="mandatoryToReadOnly_"
              left
              hide-details
              :label="$t('filter.mandatoryToRead')"
            ></v-checkbox>
            <v-checkbox
              v-if="config.userFavoritesSelectionEnabled"
              class="ml-3"
              v-model="favoritesOnly_"
              left
              hide-details
              :label="$t('filter.favoritesOnly')"
            ></v-checkbox>
            <v-checkbox
              v-if="config.userAcknowledgeSelectionEnabled"
              class="ml-3"
              v-model="toAcknowledgeOnly_"
              left
              hide-details
              :label="$t('filter.toConfirmOnly')"
            ></v-checkbox>
            <v-checkbox
              v-if="config.userDisplayMandatoryToReadAlsoWhenReadEnabled"
              class="ml-3"
              v-model="displayMandatoryToReadAlsoWhenRead_"
              left
              :label="$t('filter.displayMandatoryToReadAlsoWhenRead')"
            ></v-checkbox>
          </div>

          <div v-if="config.userFolderSelectionEnabled" class="mt-6">
            <n-list-title class="ml-3 mb-2" :text="$t('filter.folderSelection')"></n-list-title>
            <v-radio-group v-model="allFolders_" class="ml-3 my-0 pb-0">
              <v-radio :label="$t('filter.allModuleDocuments')" :value="true"></v-radio>
              <v-radio :label="$t('filter.selectedFoldersOnly')" :value="false"></v-radio>
            </v-radio-group>
          </div>
          <v-expand-transition>
            <div v-if="config.userFolderSelectionEnabled && !allFolders_" class="hide-scrollbars mx-3 mb-3 folder-selection-scroll-container">
              <v-treeview
                v-if="drawer"
                dense
                selectable
                :open-all="false"
                v-model="selectedFolders_"
                :selected-color="darkMode ? 'white' : ''"
                :items="folderTree"
                selection-type="independent"
              ></v-treeview>
            </div>
          </v-expand-transition>
        </div>
      </div>
    </template>

    <template v-slot:content @scroll="initialRendering = false">
      <div v-if="!isLoading && !filteredAndSortedDocuments.length > 0" class="d-flex align-start justify-center ma-4" style="height: 410px">
        {{ $t(config.noItemsMsg) }}
      </div>
      <div v-else>
        <div v-for="(doc, index) in documentsCurrentPage" :key="`${config.localStorageWidgetId}_${doc.id}_${index}`">
          <news-list-item v-if="doc._moduleId === 'news'" :item="doc" @onSelected="onSelected"></news-list-item>

          <video-list-item v-else-if="doc._moduleId === 'videos'" :item="doc" @onSelected="onSelected"></video-list-item>

          <base-list-item v-else @onSelected="onSelected" :file="doc" :longClickEnabled="false" :markAsReadOnSelect="doc._moduleId != 'documents'" showLocation>
            <slot name="customRow" v-if="doc.kurs">
              <span v-for="kurs in kursList(doc)" :key="kurs" :class="`tag mx-2 px-2 ${theme.accent}`">Kurs {{ kurs }}</span>
            </slot>
          </base-list-item>
        </div>
      </div>

      <!-- <n-file-extended-info v-else v-for="(file, index) in documentsCurrentPage" :key="index" :file="file" :longClickEnabled="false" @onSelected="$router.push(file._path)" /> -->
    </template>

    <!-- <template v-slot:footer v-if="height || filteredAndSortedDocuments.length >= documentsPerPage"> -->
    <template v-slot:footer>
      <div class="d-flex full-width my-auto align-center justify-end">
        <v-pagination
          v-if="hasPagination && totalPages > 1"
          small
          v-model="page"
          :color="darkMode ? 'grey darken-3' : 'grey'"
          class="mx-0 px-0 my-auto"
          :total-visible="5"
          :length="totalPages"
        ></v-pagination>
        <v-btn small v-if="totalPages > 1 && totalPages < 10" width="80" class="my-3" @click="hasPagination = !hasPagination"
          >{{ hasPagination ? 'alle' : 'Seiten' }}
        </v-btn>
        <div class="mx-3 my-3 no-wrap caption">
          {{ $t('widgets.resultItemsCount', { count: filteredAndSortedDocuments.length }) }}
        </div>
      </div>
    </template>
  </panel>
</template>

<script>
/*
 * The DocumentList is a configurable (also by user) list of documents to display.
 * the config object needs the following feature flags:

 *
 * The config object may contain the following flags to enable the user to adjust his settings:
 * - userModuleSelectionEnabled
 * - userMandatoryToReadOnlySelectionEnabled
 * - userUnreadOnlySelectionEnabled
 * - userAcknowledgeSelectionEnabled
 * - userFavoriteSelectionEnabled
 * - userFolderSelectionEnabled
 *
 * For pre-configured settings include the following attributes into the config object:
 * - selectedModules (Array of document module instances)
 * - unreadOnly (Boolean to indicate that unread documents should be displayed)
 * - favoritesOnly (Boolean to indicate that favored documents should be displayed)
 * - toAcknowledgeOnly (Boolean to indicate that documents that must be confirmed should be displayed)
 * - selectedFolders (Array of document module folder id's that are used for filtering)
 */
import Panel from '@/components/container/Panel.vue';
import common from '@/mixins/common';
import BaseListItem from '@/components/listItems/BaseListItem.vue';
import { mapState } from 'vuex';
import MorphIcon from '@/components/icons/MorphIcon.vue';
import NListTitle from '@/components/texts/NListTitle.vue';
import NTitle from '@/components/texts/NTitle.vue';
import configMixin from '../mixins/configMixin';
import ModuleSelection from './components/ModuleSelection';
import acknowledgeService from '@/services/acknowledgeService';
import mandatoryToReadService from '@/services/mandatoryToReadService';
import readService from '@/services/readService';
import fileSorters from '@/sorters/fileSorters';
import NewsListItem from '@/modules/news/NewsListItem';
import VideoListItem from '@/modules/video/VideoListItem';
import { HOURS_IN_MS } from '@/common/constants';

const HEIGHT_OFFSET = 71;
// For pre-configured settings include the following attributes into the config object:
const configAttributes = {
  selectedModules: [], //        Array of document module instances
  selectedFolders: [], //        Array of document module folder id's that are used for filtering
  unreadOnly: false, //          Boolean to indicate that unreaad documents should be displayed
  ignoreFahrdienstFilesInUnreadOnly: false, // Boolean to indicate Fahrdienst files should be omitted when unreadOnly is true
  favoritesOnly: false, //       Boolean to indicate that favored documents should be displayed
  toAcknowledgeOnly: false, //   Boolean to indicate that documents that must be confirmed should be displayed
  ignoreFahrdienstFilesInToAcknowledgeOnly: false, // Boolean to indicate Fahrdienst files should be omitted when unreadOnly is true
  mandatoryToReadOnly: false, // Boolean to indicate that only documents flagged as mandatoryToRead are displayed
  displayMandatoryToReadOnlyWhenRead: false, // Boolean to indicate that only documents flagged as mandatoryToRead are displayed and only if in read state
  displayMandatoryToReadAlsoWhenRead: false, // Boolean to indicate that mandatory to read flagged documents are displayed also when read
  showHistory: false, //         true to show the history of documents. Can be combined with selectedModules and selectedFolders
  showHistoryCount: 12, //       number of items to show when history is displayed
  showLatest: false, //          true to show the latest/newest items. Can be combined with selectedModules and selectedFolders
  showLatestCount: 12, //        number of items to show when latest items are displayed
  title: '', //                  Title of widget
};
const moduleTypeToModuleIdMapping = {
  news: 'news',
  document: 'documents',
  fahrdienst: 'fahrdienst',
  fahrdienst452: 'fahrdienst452',

  form: 'forms',
  video: 'videos',
};
export default {
  name: 'DocumentList',
  mixins: [common, configMixin],
  components: {
    Panel,
    BaseListItem,
    MorphIcon,
    NListTitle,
    ModuleSelection,
    NTitle,
    NewsListItem,
    VideoListItem,
  },

  props: {
    config: { type: Object }, // widget configuration
    // height: { type: Number }, // optional height of this widget
    isLoading: { type: Boolean, default: false },
  },
  created() {
    console.debug(`created DocumentList ${this.config.localStorageWidgetId}`);
    this.init();
  },
  updated() {
    console.debug(`updated DocumentList ${this.config.localStorageWidgetId}`);
  },
  data() {
    return {
      drawer: false,
      page: 1, //starting with 1 when using Vuetify Pagination

      height: null,
      hasPagination: true,
      title: '',
      title_: '',
      selectedModules: [], // array of selected modules (objects of title and instance attributes)
      selectedModules_: [], // array of selected modules (objects of title and instance attributes)
      selectedFolders: [],
      selectedFolders_: [],

      unreadOnly: false,
      unreadOnly_: false,
      ignoreFahrdienstFilesInUnreadOnly: false,
      ignoreFahrdienstFilesInUnreadOnly_: false,
      favoritesOnly: false,
      favoritesOnly_: false,

      toAcknowledgeOnly: false,
      toAcknowledgeOnly_: false,
      ignoreFahrdienstFilesInToAcknowledgeOnly: false,
      ignoreFahrdienstFilesInToAcknowledgeOnly_: false,

      mandatoryToReadOnly: false,
      mandatoryToReadOnly_: false,
      displayMandatoryToReadOnlyWhenRead: false,
      displayMandatoryToReadOnlyWhenRead_: false,
      displayMandatoryToReadAlsoWhenRead: false,
      displayMandatoryToReadAlsoWhenRead_: false,
      showHistory: false,
      showHistory_: false,
      showLatest: false,
      showLatest_: false,
      showLatestCount: 12,
      useFolderFiltering: false, // true to apply module document folder filtering. Set to true, when config.selectedFolders is set or the config.userFolderSelectionEnabled is true.
      useFolderFiltering_: false, // true to apply module document folder filtering. Set to true, when config.selectedFolders is set or the config.userFolderSelectionEnabled is true.
      useDocumentsFromAllModules: false, // usage of all modules is disabled if the folder selection is enabled and vice versa (config.userFolderSelectionEnabled)
      useDocumentsFromAllModules_: false, // usage of all modules is disabled if the folder selection is enabled and vice versa (config.userFolderSelectionEnabled)
      allFolders: true,
      allFolders_: true,

      widgetConfig: {},
    };
  },

  computed: {
    ...mapState('documents', ['documents', 'folders', 'foldersById', 'simpleFolderTree']),
    ...mapState('fahrdienst', { fahrdienstFiles: 'files' }),
    ...mapState('fahrplan', ['fahrplanFiles']),
    ...mapState('fahrdienst452', ['fahrdienst452File']),
    ...mapState('news', ['news']),
    ...mapState('videos', ['videos']),
    ...mapState('sendbox', ['userActionPerformed']),
    ...mapState('sync', ['dataSynchronizationComplete']),
    fullPath() {
      return this.$route.fullPath;
    },
    hasEditableProperties() {
      return (
        this.config.userTitleEditingEnabled ||
        this.config.userUnreadOnlySelectionEnabled ||
        this.config.userFavoritesSelectionEnabled ||
        this.config.userAcknowledgeSelectionEnabled ||
        this.config.userModuleSelectionEnabled ||
        this.config.userMandatoryToReadOnlySelectionEnabled ||
        this.config.userFolderSelectionEnabled
      );
    },
    documentsPerPage() {
      return this.height ? Math.min(5, Math.round((this.height - HEIGHT_OFFSET) / 80 - 0.5)) : 5;
    },
    documentsCurrentPage() {
      if (this.hasPagination) {
        let startInd = (this.page - 1) * this.documentsPerPage;
        let endInd = startInd + this.documentsPerPage;
        return this.filteredAndSortedDocuments.slice(startInd, endInd);
      }

      return this.filteredAndSortedDocuments;
    },
    totalPages() {
      return Math.ceil(this.filteredAndSortedDocuments.length / this.documentsPerPage);
    },
    calculatedHeight() {
      if (this.height) return this.height;
      return 5 * 80 + 67 + 64;
      //return this.filteredAndSortedDocuments.length < this.documentsPerPage ? 4 * 80 + 67 : this.documentsPerPage * 80 + 67 + 64;
    },
    contentHeight() {
      if (this.height) return this.height - HEIGHT_OFFSET;
      return this.filteredAndSortedDocuments.length < this.documentsPerPage ? 4 * 80 : this.documentsPerPage * 80 + 78;
    },
    unreadAndFavoritesTitle() {
      return this.$t('widgets.filter.title');
    },
    availableModules() {
      return this.modules.map((m) => {
        return { type: m.type, title: this.mx_translateIfExist(m.title), instance: m.instance };
      });
    },
    availableModulesTitles() {
      return this.modules
        .filter((m) => {
          return Object.keys(this.itemsByModuleType).includes(m.type);
        })
        .map((m) => {
          return this.mx_translateIfExist(m.title);
        })
        .sort()
        .join(', ');
    },
    // calculates the module/instance titles of those modules included within the widget
    filteredModuleTitles() {
      if (this.selectedModuleNames.length > 0) {
        return this.selectedModuleNames.join(', ');
      }
      return this.availableModulesTitles;
    },
    modulesByTypeAndInstance() {
      let byInstance = {};
      this.availableModules.forEach((m) => {
        byInstance[`${m.type}_${m.instance}`] = m;
      });
      return byInstance;
    },
    selectedModuleNames() {
      return this.selectedModules.map((m) => {
        let module = this.modulesByTypeAndInstance[`${m.type}_${m.instance}`];
        if (!module) {
          // use old configuration with only using instance and Document modules
          module = this.modulesByTypeAndInstance[`Document_${m}`];
        }

        return module ? this.mx_translateIfExist(module.title) : 'kein Titel';
      });
    },
    itemsByModuleType() {
      return {
        Fahrdienst452: this.fahrdienst452File,
        Fahrdienst: this.fahrdienstFiles,
        Fahrplan: this.fahrplanFiles,
        News: this.news,
        Videos: this.videos,
        Document: this.documents,
      };
    },
    selectedModulesByTypeAndInstance() {
      const selectedModulesByTypeAndInstance = {};
      console.debug('selectedModulesByTypeAndInstance', JSON.stringify(selectedModulesByTypeAndInstance, null, 2));

      this.selectedModules.forEach((m) => {
        const moduleId = moduleTypeToModuleIdMapping[`${m.type}`.toLowerCase()];
        selectedModulesByTypeAndInstance[`${moduleId}_${m.instance}`] = true;
      });
      return selectedModulesByTypeAndInstance;
    },

    moduleFilteredItems() {
      let items = [];

      items = this.documents;
      items = items.concat(this.fahrdienst452File);
      items = items.concat(this.fahrdienstFiles);
      items = items.concat(this.fahrplanFiles);
      items = items.concat(this.news);
      items = items.concat(this.videos);
      if (this.selectedModules.length === 0) return items;

      return items.filter((i) => !!this.selectedModulesByTypeAndInstance[`${i._moduleId}_${i.instance || i.inst}`]);
    },
    /**
     * If folders are selected, this property contains items stored directly within one of this folder. Applicable for document modules only.
     * Otherwise, all moduleFilteredItems get returned
     * @returns {T[]}
     */
    moduleFilteredItemsFilteredByFolderSelection() {
      if (this.selectedFolders.length === 0) return this.moduleFilteredItems;

      const selectedFoldersByFolderId = {};
      this.selectedFolders.forEach((folderId) => (selectedFoldersByFolderId[folderId] = true));

      return this.moduleFilteredItems.filter((i) => {
        if (i.folderIdFk) return !!selectedFoldersByFolderId[i.folderIdFk];
      });
    },

    filteredAndSortedDocuments() {
      console.debug(`filteredAndSortedDocuments for widget ${this.config.localStorageWidgetId}`);

      if (this.userActionPerformed) {
        console.debug('userActionPerformed', this.userActionPerformed);
      }
      const today = Date.now();
      const todayToBorder = today - HOURS_IN_MS * 24;

      // Filter all items
      let filtered = this.moduleFilteredItemsFilteredByFolderSelection.filter((d) => {
        /*
        if (d.referenceId) {
          let referenced = this.moduleFilteredItemsFilteredByFolderSelection.find((r) => r.id == d.referenceId);
          if (referenced) {
            // the referenced document is available, don't show reference
            return false;
          }
        }
        */

        if (!this.isWithinFromTo(d, today, todayToBorder)) return false;

        // favorites only => remove all not as favorite marked items
        if (this.favoritesOnly && (!d._userState || d._userState.favorite !== 1)) return false;

        // in case it is a history widget, remove items which have no dateread at all
        if (this.showHistory && !d._userState.dateread) return false;

        // accept unread items if unreadOnly
        if (this.unreadOnly) {
          if (this.ignoreFahrdienstFilesInUnreadOnly && ['fahrdienst452', 'fahrdienst'].includes(d._moduleId)) {
            return false;
          }
          if (!mandatoryToReadService.isMandatoryToRead(d)) {
            if (!acknowledgeService.isToAcknowledge(d) && readService.isUnread(d)) return true;
          }
        }

        // accept not confirmed items if toAcknowledgeOnly is true
        if (this.toAcknowledgeOnly) {
          console.debug(`ignoreFahrdienstFilesInToAcknowledgeOnly=${this.ignoreFahrdienstFilesInToAcknowledgeOnly}`);

          if (this.ignoreFahrdienstFilesInToAcknowledgeOnly && ['fahrdienst452', 'fahrdienst'].includes(d._moduleId)) {
            return false;
          }
          if (acknowledgeService.isToAcknowledgeAndNotRead(d)) return true;
        }

        // accept mandatory to read, but unread items if mandatoryToReadOnly=true
        if (this.mandatoryToReadOnly) {
          if (this.displayMandatoryToReadOnlyWhenRead) {
            if (mandatoryToReadService.isMandatoryToReadAndRead(d)) return true;
          } else {
            if (mandatoryToReadService.isMandatoryToReadAndNotRead(d)) return true;
          }
        }

        if (!this.unreadOnly && !this.toAcknowledgeOnly && !this.mandatoryToReadOnly) {
          return true;
        }

        return false;
      });

      if (this.showLatest) {
        let sorted = filtered.sort(fileSorters.byUpdatedLatestFirst);
        return sorted.splice(0, this.showLatestCount);
      }
      if (this.showHistory) {
        return filtered.sort(fileSorters.byDateReadLatestFirst).splice(0, this.showHistoryCount);
      }
      return filtered.sort(fileSorters.byImportance);
    },

    folderTree() {
      let tree = [];
      try {
        if (this.selectedModules.length === 0) {
          this.availableModules.forEach((m) => (tree = tree.concat(this.simpleFolderTree[m.instance])));
        } else {
          this.selectedModules.forEach((m) => (tree = tree.concat(this.simpleFolderTree[m.instance])));
        }
        return tree;
      } catch (e) {
        return [];
      }
    },
  },
  methods: {
    isWithinFromTo(d, today, todayToBorder) {
      let dateFrom, dateTo;
      let isValidAtCurrentDate = true;
      if (d.anzeige_von) {
        dateFrom = Date.parse(d.anzeige_von);
      }
      if (d.anzeige_bis) {
        dateTo = Date.parse(d.anzeige_bis);
      }

      if (this.isNumber(dateFrom) && this.isNumber(dateTo)) {
        isValidAtCurrentDate = dateFrom < today && dateTo > todayToBorder;
      } else if (this.isNumber(dateFrom)) {
        isValidAtCurrentDate = dateFrom < today;
      } else if (this.isNumber(dateTo)) {
        isValidAtCurrentDate = dateTo > todayToBorder;
      }

      if (!isValidAtCurrentDate) {
        console.debug(`Document ${d.id} ${d.title} only displayed from ${d.anzeige_von} to ${d.anzeige_bis}`);
        return false;
      }
      return true;
    },
    isNumber(value) {
      return !isNaN(value);
    },
    toggleDrawer() {
      if (!this.drawer) {
        // provide all variables to edit within the drawer form (settings)
        // do not directly edit the bound variables
        this.title_ = this.title;
        this.selectedModules_ = this.mx_clone(this.selectedModules);
        this.selectedFolders_ = this.mx_clone(this.selectedFolders);
        this.unreadOnly_ = this.unreadOnly;
        this.ignoreFahrdienstFilesInUnreadOnly_ = this.ignoreFahrdienstFilesInUnreadOnly;
        this.ignoreFahrdienstFilesInToAcknowledgeOnly_ = this.ignoreFahrdienstFilesInToAcknowledgeOnly;
        this.favoritesOnly_ = this.favoritesOnly;
        this.toAcknowledgeOnly_ = this.toAcknowledgeOnly;
        this.mandatoryToReadOnly_ = this.mandatoryToReadOnly;
        this.displayMandatoryToReadOnlyWhenRead_ = this.displayMandatoryToReadOnlyWhenRead;
        this.displayMandatoryToReadAlsoWhenRead_ = this.displayMandatoryToReadAlsoWhenRead;
        this.showHistory_ = this.showHistory;
        this.showLatest_ = this.showLatest;
        this.useFolderFiltering_ = this.useFolderFiltering;
        this.useDocumentsFromAllModules_ = this.useDocumentsFromAllModules;
        this.useDocumentsFromAllModules_ = this.useDocumentsFromAllModules;
        this.allFolders_ = this.allFolders;

        this.drawer = true;
      } else {
        this.drawer = false;
      }
    },
    onSelected(doc) {
      console.debug('onSelected doc', doc.id);
      if (doc && doc._path) {
        this.$router.push(doc._path);
      } else {
        console.debug('Cannot open document because doc._path is not defined', JSON.stringify(doc, null, 2));
      }
    },
    isMandatoryToReadAndRead(file) {
      return mandatoryToReadService.isMandatoryToReadAndRead(file);
    },
    isMandatoryToReadAndNotRead(file) {
      return mandatoryToReadService.isMandatoryToReadAndNotRead(file);
    },

    kursList(doc) {
      if (doc.kurs) {
        return doc.kurs.split(',').sort((a, b) => Number(a) - Number(b));
      }
      return [];
    },

    /**
     * Enhance configuration with information stored in local store (identified by localStorageWidgetId)
     */
    init() {
      console.debug('Init widget', this.config.localStorageWidgetId);
      // console.debug('DocumentList received config', JSON.stringify(this.config, null, 2));
      this.drawer = false;
      let storedWidgetConfigs;
      if (this.config) {
        storedWidgetConfigs = JSON.parse(localStorage.getItem('widgetConfigs') || '{}');
        let mergedWidgetConfig;
        const localStorageWidgetConfig = storedWidgetConfigs[this.config.localStorageWidgetId] || {};
        if (localStorageWidgetConfig.hasUserChanges) {
          mergedWidgetConfig = { ...this.config, ...localStorageWidgetConfig };
        } else {
          mergedWidgetConfig = { ...localStorageWidgetConfig, ...this.config };
        }

        // copy stored filter params into state
        // Write states based on mergedWidget object
        Object.keys(configAttributes).forEach((name) => {
          console.debug(`DocumentList init - write data ${name} = ${mergedWidgetConfig[name] || configAttributes[name]}`);
          if (name === 'title') {
            this.title = this.mx_translateIfExist(mergedWidgetConfig[name] || configAttributes[name]);
          } else {
            this[name] = mergedWidgetConfig[name] || configAttributes[name];
          }
        });

        this.allFolders = this.selectedFolders.length === 0;
        if (!this.config.userTitleEditingEnabled) {
          this.config.userTitleEditingEnabled = true; //default if not set
        }
        if (mergedWidgetConfig.selectedModules) {
          mergedWidgetConfig.selectedModules.forEach((m) => {
            if (!m.title) {
              let module = this.modulesByTypeAndInstance[`${m.type}_${m.instance}`];
              if (module) {
                m.title = module.title;
              }
            }
          });
          this.selectedModules = mergedWidgetConfig.selectedModules;
        }
        // disable
        this.useFolderFiltering = this.config.userFolderSelectionEnabled || this.config.selectedFolders || this.config.moduleSelection;

        this.useDocumentsFromAllModules = !this.config.useFolderFiltering;
        this.widgetConfig = mergedWidgetConfig;
      }
    },
    resetConfig() {
      let storedWidgetConfigs;
      try {
        storedWidgetConfigs = JSON.parse(localStorage.getItem('widgetConfigs') || '{}');
      } catch (err) {
        storedWidgetConfigs = {};
      }

      storedWidgetConfigs[this.config.localStorageWidgetId] = JSON.parse(JSON.stringify(this.config));

      localStorage.setItem('widgetConfigs', JSON.stringify(storedWidgetConfigs));
      this.init();
      this.drawer = false;
    },
    saveConfig() {
      console.debug('saveConfig');
      this.title = this.title_;
      this.selectedModules = this.selectedModules_;
      this.selectedFolders = this.selectedFolders_;
      this.unreadOnly = this.unreadOnly_;
      this.ignoreFahrdienstFilesInUnreadOnly = this.ignoreFahrdienstFilesInUnreadOnly_;

      this.favoritesOnly = this.favoritesOnly_;
      this.toAcknowledgeOnly = this.toAcknowledgeOnly_;
      this.ignoreFahrdienstFilesInToAcknowledgeOnly = this.ignoreFahrdienstFilesInToAcknowledgeOnly_;

      this.mandatoryToReadOnly = this.mandatoryToReadOnly_;
      this.displayMandatoryToReadOnlyWhenRead = this.displayMandatoryToReadOnlyWhenRead_;
      this.displayMandatoryToReadAlsoWhenRead = this.displayMandatoryToReadAlsoWhenRead_;
      this.showHistory = this.showHistory_;
      this.showLatest = this.showLatest_;
      this.useFolderFiltering = this.useFolderFiltering_;
      this.useDocumentsFromAllModules = this.useDocumentsFromAllModules_;
      this.useDocumentsFromAllModules = this.useDocumentsFromAllModules_;
      this.allFolders = this.allFolders_;

      let storedWidgetConfigs;
      try {
        storedWidgetConfigs = JSON.parse(localStorage.getItem('widgetConfigs') || '{}');
      } catch (err) {
        storedWidgetConfigs = {};
      }

      let newConfig = this.mx_clone(this.widgetConfig);
      Object.keys(configAttributes).forEach((name) => {
        if (this[name] === null && this.config[name]) {
          newConfig[name] = this.config[name];
          this[name] = this.config[name];
          console.debug('overwrite state', name, this.config[name]);
        } else {
          newConfig[name] = this[name];
        }
      });
      newConfig.hasUserChanges = true;
      storedWidgetConfigs[this.config.localStorageWidgetId] = newConfig;

      localStorage.setItem('widgetConfigs', JSON.stringify(storedWidgetConfigs));
      this.drawer = false;
    },
  },

  watch: {
    allFolders(newValue) {
      if (newValue) this.selectedFolders = [];
    },
    // fullPath(newPath, oldPath) {
    //   console.debug(`route change from ${oldPath} to ${newPath}, init`);
    //   if (oldPath !== newPath) {
    //     this.init();
    //   }
    // },
    dataSynchronizationComplete(newValue) {
      console.debug('dataSynchronizationComplete', newValue);
      this.init();
    },

    selectedModules_(newValue) {
      console.debug(`selectedModules_=${JSON.stringify(newValue)}`);
    },
    selectedModules(newValue) {
      console.debug(`selectedModules=${JSON.stringify(newValue)}`);
    },
  },
};
</script>
<style scoped>
.folder-selection-scroll-container {
  border: 1px solid white;
  border-radius: 10px;
}

.scrollable-drawer {
  overflow-y: scroll;
  position: absolute;
  top: 50px;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
